import localization from '@/i18n/localization'

const LOCALE_CODES = localization.locales.map((l) => l.code)

// URL migration 2026-07-17 (migration-spec/11): the canonical scheme is now
//   /workflows/<domain>/<workflow>[/<stage>|/technology/<slug>]
//   /knowledge-hub[/<slug>] /products/<group>/<slug> /solutions/<slug>
//   /companies/<slug> /events ... — no /en prefix, no /platform segment.
// normalizePath heals ANY historical form (CMS-era `/platform/...` paths still
// stored in older dumps, pre-platform prefixes, `equipment` guide segment) into
// the canonical scheme so links/canonicals/sitemaps never emit a redirecting URL.
// Order matters: most specific first.
const LEGACY_PATH_REWRITES: [RegExp, string][] = [
  [/^\/platform\/areas(?=\/|$)/, '/workflows'],
  // NOTE (IA pivot 2026-07-17): legacy product/solution paths are NOT healed here — their
  // new home (/companies/<vendor>/<slug>) needs a per-doc lookup, which the server-side
  // per-product 301s handle (next.config). Dumps store the new paths directly.
  [/^\/platform\/(?=knowledge-hub|companies|events|dashboard|user|resources|article|workflows)/, '/'],
  [/^\/platform$/, '/workflows'],
]

// Equipment guides renamed to "technology" AND lifted to the DOMAIN level
// (Veronika 2026-07-17): /workflows/<domain>/<wf>/equipment/<slug> ->
// /workflows/<domain>/technology/<slug> — one technology can serve several
// workflows in a domain, so guides hang off the domain.
const EQUIPMENT_SEGMENT = /^\/workflows\/([^/]+)\/[^/]+\/equipment(?=\/|$)/

/**
 * Rewrite any historical path form to the current canonical scheme.
 * Idempotent; a no-op for paths already canonical.
 */
export const normalizePath = (path: string | null | undefined): string | null | undefined => {
  if (!path || !path.startsWith('/')) return path
  let p = path
  // Strip a leading locale segment (the default locale serves unprefixed).
  const firstSegment = p.split('/')[1]
  if (firstSegment && LOCALE_CODES.includes(firstSegment)) {
    p = p.slice(firstSegment.length + 1) || '/'
  }
  for (const [re, replacement] of LEGACY_PATH_REWRITES) {
    if (re.test(p)) {
      p = p.replace(re, replacement)
      break
    }
  }
  p = p.replace(EQUIPMENT_SEGMENT, '/workflows/$1/technology')
  // companies -> portfolio (Veronika 2026-07-17): the vendor directory + drill-down segment
  p = p.replace(/^\/companies(?=\/|$)/, '/portfolio')
  return p
}

/**
 * Resolve an internal CMS path to a directly-servable href (HTTP 200, no
 * redirect hop). With `localePrefix: 'as-needed'` the default locale is
 * UNPREFIXED — for `en` this returns the normalized path as-is; a future
 * non-default locale gets its prefix.
 *
 * Safe to apply anywhere: idempotent and external-safe (`http(s)://`,
 * `mailto:`, `tel:`, `#anchor`, and non-rooted values pass through untouched).
 * Empty/nullish input returns `'#'` to match existing fallbacks.
 */
export const localizedHref = (
  path: string | null | undefined,
  locale?: string | null,
): string => {
  if (!path) return '#'
  if (!path.startsWith('/')) return path
  const normalized = normalizePath(path) as string
  if (!locale || locale === localization.defaultLocale) return normalized
  return `/${locale}${normalized}`
}
